/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { T, isCompactViewport } from "../../lib/globals";
import { fmtDate, compressImage } from "../../lib/formatters";
import { Btn, Modal, Field, Badge, Input, Select, Textarea } from "../ui";

function ServicePhotoAddPhoneIcon({ size=18 }) {
  return (
    <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:size,height:size,flexShrink:0}} aria-hidden="true">
      <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
        <defs>
          <linearGradient id="svcPhoneAddGrad" x1="4" y1="3" x2="20" y2="21" gradientUnits="userSpaceOnUse">
            <stop stopColor="#9BE7FF"/>
            <stop offset="1" stopColor="#2563EB"/>
          </linearGradient>
        </defs>
        <rect x="5" y="2.3" width="14" height="19.4" rx="3.7" fill="url(#svcPhoneAddGrad)" stroke="#D9F2FF" strokeWidth="1"/>
        <rect x="7.1" y="5.3" width="9.8" height="13.1" rx="2.3" fill="#EEF9FF"/>
        <rect x="9" y="3.7" width="6" height="1" rx="0.5" fill="#0F172A" opacity="0.78"/>
        <circle cx="12" cy="18.95" r="1.05" fill="#EAF8FF" opacity="0.96"/>
        <circle cx="9.3" cy="7.7" r="1.15" fill="#0F172A" opacity="0.72"/>
        <circle cx="12" cy="7.1" r="1" fill="#0F172A" opacity="0.72"/>
        <circle cx="14.7" cy="7.7" r="1.15" fill="#0F172A" opacity="0.72"/>
        <path d="M12 10.1V15.2" stroke="#1D4ED8" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M9.45 12.65H14.55" stroke="#1D4ED8" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    </span>
  );
}


function ServicePhotoStatusIcon({ size=18, hasPhotos=false }) {
  const gradId = `svcPhoneStatusGrad-${size}-${hasPhotos?1:0}`;
  return (
    <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:size,height:size,flexShrink:0,opacity:hasPhotos?1:0.55,filter:hasPhotos?"none":"grayscale(0.18) saturate(0.72)"}} aria-hidden="true">
      <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
        <defs>
          <linearGradient id={gradId} x1="4" y1="3" x2="20" y2="21" gradientUnits="userSpaceOnUse">
            <stop stopColor={hasPhotos?"#93E5FF":"#D1D5DB"}/>
            <stop offset="1" stopColor={hasPhotos?"#3B82F6":"#94A3B8"}/>
          </linearGradient>
        </defs>
        <rect x="5" y="2.5" width="14" height="19" rx="3.5" fill={`url(#${gradId})`} stroke={hasPhotos?"#D9F2FF":"#E5E7EB"} strokeWidth="1"/>
        <rect x="7" y="5.7" width="10" height="12.8" rx="2.3" fill={hasPhotos?"#EAF8FF":"#F8FAFC"} opacity="0.98"/>
        <rect x="9.2" y="3.7" width="5.6" height="1.1" rx="0.55" fill="#0F172A" opacity="0.8"/>
        <path d="M8.4 15.7L11.2 12.8L13.35 14.9L15.25 13L17 14.85V16.8H8.4V15.7Z" fill={hasPhotos?"#365A8B":"#94A3B8"}/>
        <circle cx="15.55" cy="9.7" r="1.15" fill={hasPhotos?"#3B82F6":"#CBD5E1"}/>
        <rect x="10.2" y="18.9" width="3.6" height="0.9" rx="0.45" fill="#EAF8FF" opacity="0.95"/>
        {!hasPhotos && <path d="M8.2 8.2L15.8 15.8" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" opacity="0.8"/>}
      </svg>
    </span>
  );
}


function normalizePhotoEntries(entries = []) {
  return (Array.isArray(entries) ? entries : []).map((entry, idx) => {
    if (typeof entry === "string") return { id:`ph_${idx}_${String(entry).length}`, photo: entry, thumb: entry, title: "Foto", by: "" };
    return {
      id: entry?.id || `ph_${idx}_${String(entry?.photo || entry?.url || "").length}`,
      photo: entry?.photo || entry?.url || "",
      thumb: entry?.thumb || entry?.photo || entry?.url || "",
      title: entry?.title || "Foto",
      by: entry?.by || "",
      ts: entry?.ts || entry?.createdAt || "",
      name: entry?.name || entry?.originalName || "",
      mime: entry?.mime || "image/jpeg",
    };
  }).filter(entry => entry.photo || entry.thumb || entry.name);
}


function ServicePhotosModal({ titleOptions=[], existingCount=0, onClose, onSave }) {
  const maxPhotos = 10;
  const remaining = Math.max(0, maxPhotos - Number(existingCount||0));
  const initialTitle = titleOptions[0] || "";
  const [rows, setRows] = useState([{ id: Date.now(), title: initialTitle, photo: "", thumb: "", originalName: "" }]);
  async function pickPhoto(rowId){
    const inp=document.createElement("input"); inp.type="file"; inp.accept="image/*";
    inp.onchange=async e=>{
      const f=e.target.files && e.target.files[0];
      if(!f) return;
      const photo = await compressImage(f, 1400, 0.78);
      const thumb = await compressImage(f, 320, 0.64);
      setRows(prev=>prev.map(row=>row.id===rowId?{...row,photo,thumb,originalName:f.name||""}:row));
    };
    inp.click();
  }
  const canAddRow = rows.length < remaining;
  return(
    <Modal open={true} onClose={onClose} title={<span style={{display:"inline-flex",alignItems:"center",gap:8}}><ServicePhotoAddPhoneIcon size={18}/> Adicionar Fotos</span>} wide zIndex={360}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{fontSize:12,color:T.muted}}>Selecione um título obrigatório para cada foto. As fotos ficam armazenadas no banco de dados dentro do serviço, com compressão automática para manter boa visualização. Limite: {maxPhotos} fotos por serviço.</div>
        <div style={{fontSize:12,color:T.muted,display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
          <span>Já cadastradas: <strong style={{color:T.text}}>{existingCount}</strong></span>
          <span>Você pode adicionar agora: <strong style={{color:T.text}}>{remaining}</strong></span>
        </div>
        {rows.map((row,idx)=>(
          <div key={row.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 210px auto",gap:10,alignItems:"end",padding:"10px 12px",background:T.card2,border:`1px solid ${T.border}`,borderRadius:10}}>
            <Field label={`Título ${idx+1} *`}>
              <Select value={row.title} onChange={e=>setRows(prev=>prev.map(x=>x.id===row.id?{...x,title:e.target.value}:x))}>
                <option value="">Selecione...</option>
                {(titleOptions||[]).map(opt=><option key={opt}>{opt}</option>)}
              </Select>
            </Field>
            <Field label="Foto *">
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                {row.thumb&&<img src={row.thumb} alt="" style={{width:60,height:60,objectFit:"cover",borderRadius:10,border:`1px solid ${T.border}`,boxShadow:"0 6px 16px rgba(0,0,0,.18)"}}/>}
                <button type="button" onClick={()=>pickPhoto(row.id)} style={{padding:"8px 14px",borderRadius:10,border:`1px dashed ${T.border}`,background:T.card,cursor:"pointer",color:T.muted,fontSize:13,display:"inline-flex",alignItems:"center",gap:8}}>
                  <ServicePhotoAddPhoneIcon size={16}/> {row.photo?"Trocar":"Adicionar"}
                </button>
                {row.originalName&&<span style={{fontSize:10,color:T.muted,maxWidth:180,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{row.originalName}</span>}
              </div>
            </Field>
            <button type="button" onClick={()=>setRows(prev=>prev.length===1?prev:prev.filter(x=>x.id!==row.id))} style={{background:"none",border:"none",color:rows.length===1?T.border:T.red,cursor:rows.length===1?"default":"pointer",fontSize:22,paddingBottom:8}}>×</button>
          </div>
        ))}
        <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
          <Btn variant="ghost" onClick={()=>canAddRow&&setRows(prev=>[...prev,{id:Date.now()+Math.random(),title:initialTitle,photo:"",thumb:"",originalName:""}])} disabled={!canAddRow}>{canAddRow?"+ Nova Foto":"Limite atingido"}</Btn>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
            <Btn onClick={()=>onSave(rows)}>Salvar Fotos</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { ServicePhotoAddPhoneIcon, ServicePhotoStatusIcon, normalizePhotoEntries, ServicePhotosModal };
